import { getDrawStatusKey } from '../drawStatus';
import { HangingGameSign } from './HangingGameSign';
import { BoxRoleGuide } from './BoxRoleGuide';
import { UsersIcon } from '@src/components/icons/UsersIcon';
import { MessageIcon } from '@src/components/icons/MessageIcon';
import { OrganizerIdentity } from './OrganizerIdentity';
import type { RefObject } from 'react';
import { ParticipantsGrid } from './ParticipantsGrid';
import { PencilIcon } from '@src/components/icons/PencilIcon';
import { ClockIcon } from '@src/components/icons/ClockIcon';
import { GiftIcon } from '@src/components/icons/GiftIcon';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { BoxInfo, CardInfo } from '@lub0v/santa-utils';
import { BoxPicture } from '@src/components/BoxPicture/BoxPicture';
import { boxExchangePath, boxOrganizerPath, boxMessengerPath } from '@src/routing/routes';
import { formatBudgetAmount } from '@src/site/currency';
import './BoxMainPage.css';

export function BoxMainPage({
  info,
  overviewOnly = false,
  cards,
  participantsRef,
  onChanged,
}: {
  info: BoxInfo;
  overviewOnly?: boolean;
  onChanged: () => Promise<void> | void;
  cards: CardInfo[];
  participantsRef: RefObject<HTMLElement | null>;
}) {
  const { t, i18n } = useTranslation();
  const box = info.box;
  const hasBudget = Boolean(box.useCashLimit && box.cashLimit > 0);
  const budget = hasBudget
    ? formatBudgetAmount(box.cashLimitMin, box.cashLimit, box.cashLimitCurrency, i18n.language)
    : t('game.header.budgetNone');
  const archived = Boolean(box.isArchived);
  const steps = [
    { title: 'prepare', body: box.useWish ? 'prepareWishBody' : 'prepareBody' },
    {
      title: info.isActiveDraw ? 'discover' : 'wait',
      body: info.isActiveDraw ? 'discoverBody' : 'waitBody',
    },
    { title: 'surprise', body: hasBudget ? 'surpriseBudgetBody' : 'surpriseBody' },
  ];
  return (
    <div className="box-main" data-testid="box-main">
      <div className="box-main__overview">
        <section className="box-main__mobile-hero">
          <HangingGameSign info={info} cards={cards} onChanged={onChanged} />
        </section>
        <section className="box-main__hero">
          <div className="box-main__art" aria-hidden="true">
            <BoxPicture picture={box.picture} logo={box.logo} />
          </div>
          <h1>{box.name}</h1>
          <p className="box-main__organizer">
            <OrganizerIdentity info={info} onChanged={onChanged} />
          </p>
          <span className="box-main__status">{t(getDrawStatusKey(info))}</span>
          <div className="box-main__facts">
            <div>
              <strong>{budget}</strong>
              <span>{t('game.header.budget')}</span>
            </div>
          </div>
        </section>
      </div>
      {!overviewOnly && (
        <>
          {archived ? (
            <section className="box-main__guide">
              <h2>{t('game.main.archivedTitle')}</h2>
              <p>{t('game.main.archivedBody')}</p>
            </section>
          ) : (
            <>
              {info.isAdmin && (
                <BoxRoleGuide
                  title={t('game.main.organizerGuideTitle')}
                  testId="organizer-role-guide"
                  steps={[
                    { key: 'organizerInvite', icon: <UsersIcon /> },
                    { key: 'organizerDraw', icon: <GiftIcon /> },
                    { key: 'organizerHelp', icon: <MessageIcon /> },
                  ].map((step) => ({
                    title: t(`game.main.${step.key}`),
                    icon: step.icon,
                    body: (
                      <Trans
                        i18nKey={`game.main.${step.key}Body`}
                        components={{
                          management: (
                            <Link
                              className="box-main__inline-link"
                              to={boxOrganizerPath(box.key)}
                            />
                          ),
                          messages: (
                            <Link
                              className="box-main__inline-link"
                              to={boxMessengerPath(box.key)}
                            />
                          ),
                        }}
                      />
                    ),
                  }))}
                />
              )}
              <BoxRoleGuide
                title={t('game.main.guideTitle')}
                testId="participant-role-guide"
                steps={steps.map((step, index) => ({
                  title: t(`game.main.${step.title}`),
                  icon: index === 0 ? <PencilIcon /> : index === 1 ? <ClockIcon /> : <GiftIcon />,
                  body: (
                    <Trans
                      i18nKey={`game.main.${step.body}`}
                      values={{ budget }}
                      components={{
                        budget: <strong />,
                        exchange: (
                          <Link className="box-main__inline-link" to={boxExchangePath(box.key)} />
                        ),
                      }}
                    />
                  ),
                }))}
              />
            </>
          )}
          <section
            ref={participantsRef}
            className="box-main__participants"
            aria-labelledby="box-participants-title"
          >
            <h2 id="box-participants-title">{t('game.nav.participants')}</h2>
            <ParticipantsGrid info={info} cards={cards} />
          </section>
        </>
      )}
    </div>
  );
}
